import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { SaveToProjectApi, type ProjectListResponse } from '../apis/index';
import { OpenDialogService } from './open-service';
import { Message } from '@patsnap/tahiti-core';
import { I18nService } from '@eureka/shared/core-services/src/i18n-service';
import type { ShareRole } from '@eureka/services/src/types/share';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';

import _ from 'lodash';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';

export enum SaveToProjectType {
    FMEA = 'fmea',
    SEEK = 'seek',
    MIND = 'mind',
    MATERIAL_MIND = 'material_mind',
    UPLOAD_PDF = 'UPLOAD_PDF',
    LS_FILE = 'ls_file',
    LS_FILE_CHAT = 'ls_file_chat',
    MSE_FORMULATION = 'mse_formulation'
}

export interface Project {
    title: string;
    goal?: string;
    /**
     * 业务类型埋点参数
     */
    business_type?: SaveToProjectType;
}

export interface ProjectListPublicResponse extends Project {
    work_project_id: string;
}

export interface ProjectFolder extends ProjectListPublicResponse {
    share_count?: number;
    created_at?: string;
    updated_at?: string;
    item_count?: number;
    share_type?: ShareRole;
    editable?: boolean;
    editName?: string;
    isLatest?: boolean;
}

export interface SaveToProjectOptions {
    business_id: string;
    business_node_id: string;
    business_type: SaveToProjectType;
    title: string;
    content: string;
}

export interface RemoveFromProjectInput {
    business_id: string;
    business_node_id: string;
}

export interface BatchRemoveFromProjectInput {
    business_id: string;
    business_node_ids: string[];
}

export interface SaveToProjectData extends SaveToProjectOptions {
    work_project_id: string;
}

export interface SaveToProjectService {
    saving: boolean;
    folderList: ProjectFolder[];
    selectedFolderId: string | null;
    loading: boolean;
    hasMore: boolean;
    total: number;
    currentPage: number;
    getFolderList(): Promise<ProjectFolder[]>;
    getFolderListWithPagination(page?: number, reset?: boolean): Promise<ProjectListResponse>;
    loadMore(): Promise<void>;
    open(options: SaveToProjectOptions): Promise<unknown>;
    toggleEditable(folderId: string): void;
    handleEditFinish(folder: ProjectFolder, createBusinessType: SaveToProjectType): void;
    addToFolder(data: SaveToProjectData): Promise<void>;
    removeFromProject(data: RemoveFromProjectInput): Promise<void>;
}

@injectable()
export class SaveToProjectServiceImpl implements SaveToProjectService {
    public selectedFolderId: string | null = null;
    public saving = false;
    public folderList: ProjectFolder[] = [];
    public loading = false;
    public loadingMore = false;
    public hasMore = true;
    public total = 0;
    public currentPage = 0;
    private readonly _pageSize = 20;

    constructor(
        @inject(SaveToProjectApi) private readonly _api: SaveToProjectApi,
        @inject(OpenDialogService) private readonly _openDialogService: OpenDialogService,
        @inject(Message) private readonly _message: Message,
        @inject(I18nService) private readonly _i18nService: I18nService,
        @inject(BiTrackService) private readonly _biTrackService: BiTrackService
    ) {
    }

    private _reHandleLatestUpdate(response?: ProjectFolder[] | []) {
        const sortedProjectsNewestFirst = _.orderBy(response, ['updated_at'], ['desc']);
        const latestProjectId = sortedProjectsNewestFirst[0]?.work_project_id;
        this.folderList = (response || []).map(folder => folder.work_project_id === latestProjectId ? { ...folder, isLatest: true } : { ...folder, isLatest: false })
    }

    // 获取项目文件夹列表
    async getFolderList(): Promise<ProjectFolder[]> {
        const response = await this._api.getFolderList();
        this._reHandleLatestUpdate(response)
        this.selectedFolderId = this.folderList[0]?.work_project_id || null;
        return this.folderList;
    }

    // 分页获取项目文件夹列表
    async getFolderListWithPagination(page = 1, reset = false): Promise<ProjectListResponse> {
        const isInitialLoad = page === 1 || reset;

        if (isInitialLoad) {
            this.loading = true;
        } else {
            this.loadingMore = true;
        }

        try {
            const response = await this._api.getFolderListWithPagination({
                page,
                limit: this._pageSize,
            });

            this.total = response.total;
            this.currentPage = page;
            this.hasMore = page * this._pageSize < response.total;

            if (reset || page === 1) {
                this.folderList = response.result || []
            } else {
                this.folderList = [...this.folderList, ...(response.result || [])];
            }

            this._reHandleLatestUpdate(this.folderList);

            // 如果没有选中项目且有项目列表，自动选择第一个
            if (!this.selectedFolderId && this.folderList.length > 0) {
                this.selectedFolderId = this.folderList[0].work_project_id;
            }

            return response;
        } finally {
            if (isInitialLoad) {
                this.loading = false;
            } else {
                this.loadingMore = false;
            }
        }
    }

    // 加载更多数据
    async loadMore(): Promise<void> {
        if (this.loading || this.loadingMore || !this.hasMore) return;

        await this.getFolderListWithPagination(this.currentPage + 1);
    }

    private _createFolder(title: string, type: SaveToProjectType): Promise<ProjectFolder> {
        return this._api.createFolder({ title, business_type: type }).then(res => {
            this.folderList.unshift(res);
            this.selectedFolderId = res.work_project_id
            this._reHandleLatestUpdate(this.folderList)
            return res;
        }).finally(() => {
            this.folderList.pop()
        });
    }

    private _editFolder(id: string, title: string): Promise<ProjectFolder> {
        return this._api.editFolder({
            work_project_id: id,
            title,
        }).then(res => {
            this.folderList = this.folderList.map(folder => folder.work_project_id === id ? res : folder);
            this._reHandleLatestUpdate(this.folderList)
            return res;
        });
    }

    toggleEditable(folderId: string) {
        this.folderList = this.folderList.map(folder => folder.work_project_id === folderId ? { ...folder, editable: !folder.editable } : folder);
    }

    async handleEditFinish(folder: ProjectFolder, createBusinessType: SaveToProjectType) {
        if (!folder.work_project_id) {
            folder.editName?.trim() ? await this._createFolder(folder.editName, createBusinessType) : this.folderList.pop()
            this._biTrackService.biTrack(EurekaTrackEventType.WORK_PROJECT, {
                sourceType: 'create-project-from-dialog',
                otherParam: createBusinessType,
            });
        } else {
            if (folder.editName?.trim() && folder.editName !== folder.title) {
                await this._editFolder(folder.work_project_id, folder.editName)
            }
        }
        this.toggleEditable(folder.work_project_id)
    }

    removeFromProject(data: RemoveFromProjectInput): Promise<void> {
        return this._api.removeFromProject(data).then(() => {
            this._message.success({
                message: this._i18nService.t('saveToProject.removeSuccess'),
            });
        })
    }

    addToFolder(data: SaveToProjectData): Promise<void> {
        this.saving = true;
        return this._api.addToFolder(data).then(() => {
            this._message.success({
                message: `${this._i18nService.t('saveToProject.addSuccess', {
                    content: data.work_project_id,
                })}`,
                showClose: true,
                dangerouslyUseHTMLString: true,
            })
        }).finally(() => {
            this.saving = false;
        });
    }

    async open(options: SaveToProjectOptions): Promise<unknown> {
        return this._openDialogService.open(options).finally(() => {
            this.selectedFolderId = null;
        })
    }
}

// 导出服务标识符
export const SaveToProjectService = Symbol('SaveToProjectService');
